import { Vector2 } from "../../common/vector2.js";
import type { UIElement } from "./ui_element.js";

export type ScratchCanvas = { canvas: OffscreenCanvas | HTMLCanvasElement; ctx: CanvasRenderingContext2D };

/**
 * An off-page canvas for caching UI. OffscreenCanvas first: on device a fresh
 * detached <canvas> cost ~200 ms on its first use while OffscreenCanvas did
 * not (uiPerf.bench()). The UI draws through the CanvasRenderingContext2D
 * surface both share, so the context is handed out under that type.
 */
export function createScratchCanvas( width: number, height: number ): ScratchCanvas | null
{
  const w = Math.max( 1, Math.ceil( width ) );
  const h = Math.max( 1, Math.ceil( height ) );
  if ( typeof OffscreenCanvas !== "undefined" )
  {
    const canvas = new OffscreenCanvas( w, h );
    const ctx = canvas.getContext( "2d" ) as unknown as CanvasRenderingContext2D | null;
    if ( ctx ) return { canvas, ctx };
  }
  const canvas = document.createElement( "canvas" );
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext( "2d" );
  return ctx ? { canvas, ctx } : null;
}

/**
 * Renders one element outside the normal tree walk, at the spot the walk would
 * have put it: under its parent's absolute origin, into any context. Works for
 * an element that only has a parent pointer and is not in the parent's
 * children, which is how per-frame animated sheets are kept from dirtying
 * the whole tree. Parent scale and opacity are not applied.
 */
export function renderElementAt( ctx: CanvasRenderingContext2D, element: UIElement ): void
{
  const parent = element.getParentElement();
  const origin = parent ? parent.getAbsolutePosition() : new Vector2( 0, 0 );
  const own_ctx = element.getContext();

  ctx.save();
  ctx.translate( origin.x, origin.y );
  if ( own_ctx !== ctx ) element.setContextRecursive( ctx );
  element.invalidateSubtree();
  element.render();
  // Not every element clears its children's flags when it paints them (the
  // nine-slice draws its parts itself); a leftover flag would read as a
  // change on the next update and repaint the layer every frame.
  element.skipRenderSubtree();
  if ( own_ctx !== ctx ) element.setContextRecursive( own_ctx );
  ctx.restore();
}

let shaders_warmed = false;

/**
 * Draws one of each kind of 2D op the UI uses (gradients, blurred shadows,
 * outlined and shadowed text, clips, patterns, composite modes, filters) on an
 * off-page canvas and flushes it. The GPU compiles a program the first time a
 * session draws each kind; on device that landed on a screen's first frame as
 * ~100 ms outside JS (leaderboard, 1+2), while repeat opens were clean.
 * Runs once per session.
 */
export function warmCanvas2DShaders(): void
{
  if ( shaders_warmed || typeof OffscreenCanvas === "undefined" ) return;
  shaders_warmed = true;

  const canvas = new OffscreenCanvas( 256, 256 );
  const ctx = canvas.getContext( "2d" );
  const src = new OffscreenCanvas( 32, 32 );
  const src_ctx = src.getContext( "2d" );
  if ( !ctx || !src_ctx ) return;
  src_ctx.fillStyle = "#3a7";
  src_ctx.fillRect( 0, 0, 32, 32 );

  const linear = ctx.createLinearGradient( 0, 0, 0, 256 );
  linear.addColorStop( 0, "#1052FF" );
  linear.addColorStop( 1, "#0A3EC7" );
  const radial = ctx.createRadialGradient( 128, 128, 4, 128, 128, 120 );
  radial.addColorStop( 0, "#fff" );
  radial.addColorStop( 1, "rgba(255,255,255,0)" );
  const pattern = ctx.createPattern( src, "repeat" );

  ctx.fillStyle = linear;
  ctx.fillRect( 0, 0, 256, 256 );
  ctx.fillStyle = radial;
  ctx.fillRect( 0, 0, 256, 256 );
  if ( pattern )
  {
    ctx.fillStyle = pattern;
    ctx.fillRect( 0, 0, 64, 64 );
  }

  ctx.globalAlpha = 0.6;
  ctx.drawImage( src, 10, 10, 90, 70 );
  ctx.globalAlpha = 1;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage( src, 2, 2, 20, 20, 100, 100, 140, 120 );

  for ( const blur of [ 1, 3, 6, 12, 24, 48 ] )
  {
    ctx.shadowColor = "rgba(13,4,72,0.8)";
    ctx.shadowBlur = blur;
    ctx.shadowOffsetY = 4;
    ctx.fillStyle = "#FDA218";
    ctx.beginPath();
    ctx.roundRect( 20, 20, 120, 60, 16 );
    ctx.fill();
    ctx.drawImage( src, 150, 150, 40, 40 );
    ctx.font = `900 ${20 + blur}px Geologica, 'Arial Black', Arial, sans-serif`;
    ctx.fillText( "Warm 123", 10, 200 );
  }
  ctx.shadowColor = "rgba(0,0,0,0)";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  ctx.font = "900 40px Geologica, 'Arial Black', Arial, sans-serif";
  ctx.lineJoin = "round";
  ctx.lineWidth = 10;
  ctx.strokeStyle = linear;
  ctx.strokeText( "Warm 123", 10, 120 );
  ctx.strokeStyle = "#003F87";
  ctx.strokeText( "Warm 123", 10, 160 );
  ctx.fillStyle = linear;
  ctx.fillText( "Warm 123", 10, 120 );
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.roundRect( 30, 30, 150, 80, 20 );
  ctx.stroke();

  ctx.save();
  ctx.beginPath();
  ctx.roundRect( 40, 40, 160, 160, 24 );
  ctx.clip();
  ctx.drawImage( src, 0, 0, 256, 256 );
  ctx.fillRect( 0, 0, 128, 128 );
  ctx.restore();
  ctx.save();
  ctx.beginPath();
  ctx.rect( 10, 10, 100, 100 );
  ctx.clip();
  ctx.fillText( "Warm", 20, 60 );
  ctx.restore();

  for ( const op of [ "source-atop", "destination-out" ] as const )
  {
    ctx.globalCompositeOperation = op;
    ctx.fillStyle = "#fff";
    ctx.fillRect( 50, 50, 100, 100 );
    ctx.drawImage( src, 60, 60, 80, 80 );
  }
  ctx.globalCompositeOperation = "source-over";

  for ( const filter of [ "hue-rotate(90deg)", "blur(4px)", "brightness(0.6) saturate(0.5)" ] )
  {
    ctx.filter = filter;
    ctx.drawImage( src, 100, 20, 64, 64 );
  }
  ctx.filter = "none";

  canvas.transferToImageBitmap().close();
}
